import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Button } from '../../components/ui/button';
import { CenteredSpinner } from '../../components/ui/Spinner';
import { ApiError } from '../../lib/api';
import {
  getEligibleContactSends,
  type EligibilityTriple,
} from '../../lib/contactEligibility';
import type { Contact } from '../../types/contact';
import { listContacts } from '../contacts/api';
import { bulkSendPreview, type PreviewSendChannel, type SendResult } from './sendApi';

// Ad Preview Stage 3 §3.2. Bulk-send modal opened from the Master
// Grid bulk-action bar. Aggregates the selected rows PER CONTACT
// (multi-location amendment §A1): the counts shown are unique
// recipients, not rows.
//
// Eligibility preview is computed client-side from the full contact
// list (one listContacts() call, filtered to the selected
// locations) using the same getEligibleContactSends helper the
// server runs. The server re-resolves everything on submit -- this
// is purely the modal's "you're about to send N messages to M
// people" summary. Per-row link validity (expiry/revocation) is the
// server's call; rows that fail land in the response's rejectedRows.

type RowDescriptor = {
  rowId: string;
  /** The row's location accountId -- contacts are linked by this. */
  locationAccountId: string;
  /** A stable preview-link id for de-dup in the eligibility helper.
   *  Two rows sharing a location still count distinctly. */
  previewLinkId: string;
  locationName: string;
};

type Props = {
  rolloutId: string;
  rows: RowDescriptor[];
  /** Sample email subject shown in the modal (campaign label). */
  sampleSubject?: string | null;
  onClose: () => void;
  onSent: (result: SendResult) => void;
};

export function BulkSendPreviewModal({
  rolloutId,
  rows,
  sampleSubject,
  onClose,
  onSent,
}: Props) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [channels, setChannels] = useState<Set<PreviewSendChannel>>(new Set());
  const [initialised, setInitialised] = useState(false);

  // One query for all contacts; filter to the selected locations
  // client-side. Bounded collection -- cheaper than N per-location
  // queries on modal open.
  const contactsQuery = useQuery({
    queryKey: ['contacts'],
    queryFn: () => listContacts(),
  });

  const summary = useMemo(() => {
    if (!contactsQuery.data) {
      return { emailCount: 0, smsCount: 0, notIncluded: 0 };
    }
    const contactsByAccount = new Map<string, Contact[]>();
    for (const c of contactsQuery.data) {
      for (const link of c.accountLinks) {
        const list = contactsByAccount.get(link.accountId) ?? [];
        list.push(c);
        contactsByAccount.set(link.accountId, list);
      }
    }

    const triples: EligibilityTriple<
      { accountId: string },
      { id: string },
      Contact
    >[] = [];
    // Contacts linked to a selected location at all (denominator for
    // "not included"). Keyed by contact id.
    const linkedContactIds = new Set<string>();
    for (const row of rows) {
      const locContacts = contactsByAccount.get(row.locationAccountId) ?? [];
      for (const contact of locContacts) {
        linkedContactIds.add(contact.id);
        triples.push({
          row: { accountId: row.locationAccountId },
          previewLink: { id: row.previewLinkId },
          contact,
        });
      }
    }

    const result = getEligibleContactSends(triples, ['email', 'sms']);
    // Contacts that pass the per-link gate on at least one channel.
    const included = new Set<string>([
      ...result.email.keys(),
      ...result.sms.keys(),
    ]);
    const notIncluded = [...linkedContactIds].filter((id) => !included.has(id)).length;
    return {
      emailCount: result.email.size,
      smsCount: result.sms.size,
      notIncluded,
    };
  }, [contactsQuery.data, rows]);

  useEffect(() => {
    if (initialised || !contactsQuery.data) return;
    const ch = new Set<PreviewSendChannel>();
    if (summary.emailCount > 0) ch.add('email');
    if (summary.smsCount > 0) ch.add('sms');
    setChannels(ch);
    setInitialised(true);
  }, [initialised, contactsQuery.data, summary]);

  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const sendMut = useMutation({
    mutationFn: () =>
      bulkSendPreview(rolloutId, {
        rowIds: rows.map((r) => r.rowId),
        channels: [...channels],
      }),
    onSuccess: (result) => {
      onSent(result);
      onClose();
    },
    onError: (err: Error) => {
      setError(err instanceof ApiError ? err.message : 'Failed to queue bulk send.');
    },
  });

  const toggleChannel = (ch: PreviewSendChannel) => {
    setChannels((prev) => {
      const next = new Set(prev);
      if (next.has(ch)) next.delete(ch);
      else next.add(ch);
      return next;
    });
  };

  // Total message count = email recipients (if email ticked) + SMS
  // recipients (if SMS ticked). A contact on both counts twice
  // (matches spec §3.2).
  const messageCount =
    (channels.has('email') ? summary.emailCount : 0) +
    (channels.has('sms') ? summary.smsCount : 0);

  const canSend = messageCount > 0 && channels.size > 0 && !sendMut.isPending;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
      <div
        className="w-full max-w-xl rounded-md bg-white shadow-xl max-h-[90vh] overflow-y-auto"
        role="dialog"
        aria-modal="true"
        aria-labelledby="bulk-send-title"
      >
        <div className="p-6 space-y-4">
          <div className="space-y-1">
            <h2 id="bulk-send-title" className="text-lg font-semibold text-slate-900">
              Send previews to franchisees
            </h2>
            <p className="text-sm text-slate-500">
              {rows.length} selected {rows.length === 1 ? 'row' : 'rows'}. Each
              franchisee contact gets ONE message per channel covering all their
              locations.
            </p>
          </div>

          {contactsQuery.isLoading && (
            <div className="py-4">
              <CenteredSpinner />
            </div>
          )}

          {contactsQuery.data && (
            <>
              <div className="rounded border border-slate-200 bg-slate-50 p-3 text-sm space-y-1">
                <p className="font-medium text-slate-700">Eligible recipients</p>
                <ul className="text-slate-600 space-y-0.5">
                  <li>📧 {summary.emailCount} contacts via Email</li>
                  <li>📱 {summary.smsCount} contacts via SMS</li>
                  {summary.notIncluded > 0 && (
                    <li className="text-slate-400">
                      {summary.notIncluded} contacts not included (Campaign
                      previews not enabled)
                    </li>
                  )}
                </ul>
              </div>

              {summary.emailCount === 0 && summary.smsCount === 0 ? (
                <p className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                  No eligible recipients across the selected rows. Enable
                  “Campaign previews” + a comms channel on the franchisee
                  contacts first.
                </p>
              ) : (
                <div>
                  <p className="text-xs font-medium text-slate-700 mb-1">Channels</p>
                  <div className="flex gap-4 text-sm">
                    <label className="inline-flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={channels.has('email')}
                        onChange={() => toggleChannel('email')}
                        disabled={summary.emailCount === 0}
                      />
                      📧 Email
                    </label>
                    <label className="inline-flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={channels.has('sms')}
                        onChange={() => toggleChannel('sms')}
                        disabled={summary.smsCount === 0}
                      />
                      📱 SMS
                    </label>
                  </div>
                </div>
              )}

              {sampleSubject && (
                <div className="text-sm">
                  <p className="text-xs font-medium text-slate-700">Sample email subject</p>
                  <p className="text-slate-600 italic">
                    “Ad Previews Ready — {sampleSubject}”
                  </p>
                </div>
              )}

              <p className="text-xs text-slate-500">
                Reply-To on emails goes to your Kyck address. Sends process in
                the background.
              </p>
            </>
          )}

          {error && (
            <p className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </p>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              ref={closeRef}
              type="button"
              onClick={onClose}
              disabled={sendMut.isPending}
              className="rounded border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <Button type="button" onClick={() => sendMut.mutate()} disabled={!canSend}>
              {sendMut.isPending
                ? 'Queuing…'
                : `Send ${messageCount} message${messageCount === 1 ? '' : 's'}`}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
